/**
 * @theme/utilities — small everyday helpers used across components.
 *
 * Zero dependencies, no side effects on import (except installing scheduler polyfills lazily).
 */

/* -------------------------------------------------------------------------- */
/* Scheduling                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * requestIdleCallback polyfill (Safari doesn't ship native rIC).
 * @param {IdleRequestCallback} cb
 * @param {IdleRequestOptions} [opts]
 * @returns {number}
 */
export const requestIdle = (typeof window !== 'undefined' && 'requestIdleCallback' in window)
  ? window.requestIdleCallback.bind(window)
  : (cb, opts = {}) => {
      const start = performance.now();
      const timeout = opts.timeout ?? 50;
      return /** @type {any} */ (setTimeout(() => cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, timeout - (performance.now() - start)),
      }), 1));
    };

/**
 * cancelIdleCallback polyfill.
 * @param {number} id
 */
export const cancelIdle = (typeof window !== 'undefined' && 'cancelIdleCallback' in window)
  ? window.cancelIdleCallback.bind(window)
  : clearTimeout;

/**
 * Yield to the main thread. Resolves on the next idle callback.
 * @returns {Promise<void>}
 */
export function yieldToMainThread() {
  return new Promise((resolve) => requestIdle(() => resolve()));
}

/**
 * scheduler.yield() polyfill. Returns a promise that resolves after a microtask + RAF + idle.
 * Use sparingly — prefer requestIdle for non-visual work and rAF for visual work.
 */
export const scheduler = {
  yield: typeof globalThis.scheduler?.yield === 'function'
    ? globalThis.scheduler.yield.bind(globalThis.scheduler)
    : () => new Promise((resolve) => requestAnimationFrame(() => requestIdle(() => resolve()))),
};

/* -------------------------------------------------------------------------- */
/* debounce / throttle                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Trailing-edge debounce. Returned function exposes `.cancel()` and `.flush()`.
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @param {number} wait
 * @returns {F & { cancel: () => void, flush: () => void }}
 */
export function debounce(fn, wait = 200) {
  /** @type {ReturnType<typeof setTimeout> | null} */
  let timer = null;
  /** @type {any[] | null} */
  let pendingArgs = null;
  /** @type {any} */
  let pendingThis = null;
  const wrapped = function (/** @type {any[]} */ ...args) {
    pendingArgs = args;
    pendingThis = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      const a = pendingArgs;
      const t = pendingThis;
      pendingArgs = pendingThis = null;
      if (a) fn.apply(t, a);
    }, wait);
  };
  wrapped.cancel = () => { if (timer) clearTimeout(timer); timer = null; pendingArgs = pendingThis = null; };
  wrapped.flush = () => {
    if (!timer) return;
    clearTimeout(timer); timer = null;
    const a = pendingArgs; const t = pendingThis;
    pendingArgs = pendingThis = null;
    if (a) fn.apply(t, a);
  };
  return /** @type {any} */ (wrapped);
}

/**
 * Leading-edge throttle. Returned function exposes `.cancel()`.
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @param {number} wait
 * @returns {F & { cancel: () => void }}
 */
export function throttle(fn, wait = 200) {
  /** @type {number} */
  let last = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let trailing = null;
  /** @type {any[] | null} */
  let pendingArgs = null;
  /** @type {any} */
  let pendingThis = null;
  const wrapped = function (/** @type {any[]} */ ...args) {
    const now = Date.now();
    const remaining = wait - (now - last);
    pendingArgs = args; pendingThis = this;
    if (remaining <= 0) {
      if (trailing) { clearTimeout(trailing); trailing = null; }
      last = now;
      fn.apply(this, args);
      pendingArgs = pendingThis = null;
    } else if (!trailing) {
      trailing = setTimeout(() => {
        last = Date.now();
        trailing = null;
        const a = pendingArgs; const t = pendingThis;
        pendingArgs = pendingThis = null;
        if (a) fn.apply(t, a);
      }, remaining);
    }
  };
  wrapped.cancel = () => { if (trailing) clearTimeout(trailing); trailing = null; pendingArgs = pendingThis = null; };
  return /** @type {any} */ (wrapped);
}

/* -------------------------------------------------------------------------- */
/* Media-query helpers                                                        */
/* -------------------------------------------------------------------------- */

/* The breakpoint set declared in CLAUDE.md / ARCHITECTURE.md. */
export const Breakpoints = Object.freeze({
  sm: 576,
  md: 750,   // primary mobile/desktop split
  lg: 992,
  xl: 1200,
  xxl: 1400,
});

/** @returns {boolean} true when the viewport is below the primary 750px breakpoint. */
export function isMobileBreakpoint() {
  return matchMedia(`(max-width: ${Breakpoints.md - 1}px)`).matches;
}

/** @returns {boolean} */
export function isDesktopBreakpoint() {
  return matchMedia(`(min-width: ${Breakpoints.md}px)`).matches;
}

/** @returns {boolean} crude touch-device detection. */
export function isTouchDevice() {
  return matchMedia('(pointer: coarse)').matches && !matchMedia('(hover: hover)').matches;
}

/** @returns {boolean} */
export function prefersReducedMotion() {
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** @returns {boolean} */
export function prefersReducedTransparency() {
  return matchMedia('(prefers-reduced-transparency: reduce)').matches;
}

/** @returns {boolean} */
export function prefersMoreContrast() {
  return matchMedia('(prefers-contrast: more)').matches;
}

/* -------------------------------------------------------------------------- */
/* Fetch presets                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Build a fetch config for the Shopify Cart API. Defaults to JSON in/out.
 * @param {'json'|'javascript'} [type='json']
 * @returns {RequestInit}
 */
export function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': type === 'javascript' ? 'application/javascript' : 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  };
}

/* -------------------------------------------------------------------------- */
/* DOM helpers                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Returns a promise that resolves on the next `animationend` for the element.
 * Resolves immediately (timeout 0) when prefers-reduced-motion is reduce.
 * @param {Element} el
 * @param {number} [maxWait=2000]
 * @returns {Promise<void>}
 */
export function onAnimationEnd(el, maxWait = 2000) {
  if (prefersReducedMotion()) return Promise.resolve();
  return new Promise((resolve) => {
    /** @type {ReturnType<typeof setTimeout>} */
    let timer;
    const done = () => { el.removeEventListener('animationend', done); clearTimeout(timer); resolve(); };
    el.addEventListener('animationend', done, { once: true });
    timer = setTimeout(done, maxWait);
  });
}

/**
 * Wraps the View Transitions API. Falls back to immediate apply when unsupported or reduced-motion.
 * @param {() => void | Promise<void>} updateDom
 * @returns {Promise<void>}
 */
export async function startViewTransition(updateDom) {
  if (prefersReducedMotion() || typeof document.startViewTransition !== 'function') {
    await updateDom();
    return;
  }
  const transition = document.startViewTransition(() => updateDom());
  try { await transition.finished; } catch { /* user navigated away — ignore */ }
}

/**
 * Returns elements currently intersecting the viewport.
 * @param {string | NodeListOf<Element> | Element[]} target
 * @param {number} [margin=0]
 * @returns {Element[]}
 */
export function getVisibleElements(target, margin = 0) {
  /** @type {Element[]} */
  const list = typeof target === 'string'
    ? Array.from(document.querySelectorAll(target))
    : Array.from(/** @type {any} */ (target));
  return list.filter((el) => {
    const r = el.getBoundingClientRect();
    return (
      r.bottom >= -margin &&
      r.right >= -margin &&
      r.top <= (window.innerHeight || document.documentElement.clientHeight) + margin &&
      r.left <= (window.innerWidth || document.documentElement.clientWidth) + margin
    );
  });
}

/**
 * @param {Element} el
 * @param {Event} event
 * @returns {boolean} true if the event target is outside `el` (handles Shadow DOM via composedPath).
 */
export function isClickedOutside(el, event) {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  if (path.length) return !path.includes(el);
  return !el.contains(/** @type {Node} */ (event.target));
}

/**
 * Update the <meta name="theme-color"> tag.
 * @param {string} color  hex / oklch / hsl / rgb
 */
export function changeMetaThemeColor(color) {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', color);
}

/**
 * Resolves when an image has been decoded. Useful before swapping a hero image.
 * @param {string} src
 * @param {string} [crossorigin]
 * @returns {Promise<HTMLImageElement>}
 */
export function preloadImage(src, crossorigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossorigin) img.crossOrigin = crossorigin;
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

/* -------------------------------------------------------------------------- */
/* Editor helpers                                                              */
/* -------------------------------------------------------------------------- */

const __editorOnce = new Set();

/**
 * Run `fn` once per theme-editor session for a given key (uses sessionStorage).
 * @param {string} key
 * @param {() => void} fn
 */
export function oncePerEditorSession(key, fn) {
  if (__editorOnce.has(key)) return;
  __editorOnce.add(key);
  try {
    if (sessionStorage.getItem(`__theme_once__${key}`)) return;
    sessionStorage.setItem(`__theme_once__${key}`, '1');
  } catch { /* storage may be denied */ }
  fn();
}

export function isDesignMode() {
  return Boolean(window.Shopify?.designMode);
}

/* -------------------------------------------------------------------------- */
/* Classes                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Pause every playable media element on the page (video, audio, model-viewer, deferred-media iframes).
 * Replaces shrine-main.js's global `pauseAllMedia()` helper.
 */
export function pauseAllMedia() {
  for (const v of document.querySelectorAll('video')) { try { v.pause(); } catch { /* noop */ } }
  for (const a of document.querySelectorAll('audio')) { try { a.pause(); } catch { /* noop */ } }
  for (const m of document.querySelectorAll('model-viewer')) {
    try { /** @type {any} */ (m).pause?.(); } catch { /* noop */ }
  }
  for (const iframe of document.querySelectorAll('iframe.js-youtube, iframe.js-vimeo')) {
    try {
      const message = iframe.classList.contains('js-vimeo')
        ? '{"method":"pause"}'
        : '{"event":"command","func":"pauseVideo","args":""}';
      /** @type {HTMLIFrameElement} */ (iframe).contentWindow?.postMessage(message, '*');
    } catch { /* noop */ }
  }
}

/* For backwards compat with components that expect a global. */
if (typeof window !== 'undefined' && !('pauseAllMedia' in window)) {
  /** @type {any} */ (window).pauseAllMedia = pauseAllMedia;
}

/**
 * ResizeNotifier — a thin wrapper around ResizeObserver that throttles by rAF.
 * Use when you need many elements to react to size changes without thrashing layout.
 */
export class ResizeNotifier {
  /** @param {(entries: ResizeObserverEntry[]) => void} cb */
  constructor(cb) {
    /** @type {ResizeObserverEntry[]} */
    this.queue = [];
    this.cb = cb;
    this.scheduled = false;
    this.observer = new ResizeObserver((entries) => {
      this.queue.push(...entries);
      if (this.scheduled) return;
      this.scheduled = true;
      requestAnimationFrame(() => {
        this.scheduled = false;
        const list = this.queue.splice(0);
        this.cb(list);
      });
    });
  }
  /** @param {Element} el */
  observe(el) { this.observer.observe(el); }
  /** @param {Element} el */
  unobserve(el) { this.observer.unobserve(el); }
  disconnect() { this.observer.disconnect(); }
}
